import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildQuiz, type Deck, type QuizQuestion } from '../types';

interface Props {
  deck: Deck;
  t: (key: string) => string;
  /** Appelé une seule fois, à la fin de la partie. */
  onComplete: (payload: { score: number; total: number; xp: number; correctIds: string[]; wrongIds: string[] }) => void;
  onExit: () => void;
}

const CELEBRATIONS = ['Bravo !', 'Excellent !', 'Parfait !', 'Super !', 'Continuez !'];
const COLORS = ['bg-primary-500', 'bg-accent-500', 'bg-secondary-500', 'bg-foreground-300'];

function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 22 }, (_, index) => ({
        left: Math.random() * 100,
        delay: Math.random() * 0.35,
        size: 6 + Math.random() * 7,
        color: COLORS[index % COLORS.length],
        round: index % 3 === 0,
      })),
    [],
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden z-20" aria-hidden="true">
      {pieces.map((piece, index) => (
        <span
          key={index}
          className={`zf-confetti ${piece.color} ${piece.round ? 'rounded-full' : ''}`}
          style={{
            left: `${piece.left}%`,
            width: piece.size,
            height: piece.size,
            animationDelay: `${piece.delay}s`,
          }}
        ></span>
      ))}
    </div>
  );
}

export default function QuizRunner({ deck, t, onComplete, onExit }: Props) {
  const total = Math.min(8, deck.cards.length);
  const [questions] = useState<QuizQuestion[]>(() => buildQuiz(deck, total));
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [lives, setLives] = useState(3);
  const [correctCount, setCorrectCount] = useState(0);
  const [correctIds, setCorrectIds] = useState<string[]>([]);
  const [wrongIds, setWrongIds] = useState<string[]>([]);
  const [finished, setFinished] = useState(false);
  const [celebrate, setCelebrate] = useState<string | null>(null);
  const reported = useRef(false);
  const timer = useRef<number | null>(null);

  const current = questions[index];
  const totalQuestions = questions.length;

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const finish = useCallback(
    (finalCorrect: number, finalWrong: string[], finalCorrectIds: string[]) => {
      setFinished(true);
      const xp = finalCorrect * 10 + (finalWrong.length === 0 ? 15 : 0);
      if (!reported.current) {
        reported.current = true;
        onComplete({ score: finalCorrect, total: totalQuestions, xp, correctIds: finalCorrectIds, wrongIds: finalWrong });
      }
    },
    [onComplete, totalQuestions],
  );

  const answer = (optionIndex: number) => {
    if (!current || feedback) return;
    const isCorrect = optionIndex === current.correctIndex;
    setSelected(optionIndex);
    setFeedback(isCorrect ? 'correct' : 'wrong');

    let nextCorrect = correctCount;
    let nextIds = correctIds;
    let nextWrong = wrongIds;

    if (isCorrect) {
      nextCorrect = correctCount + 1;
      nextIds = [...correctIds, current.cardId];
      setCorrectCount(nextCorrect);
      setCorrectIds(nextIds);
      setCelebrate(CELEBRATIONS[Math.floor(Math.random() * CELEBRATIONS.length)]);
    } else {
      nextWrong = [...wrongIds, current.cardId];
      setWrongIds(nextWrong);
      setLives((value) => Math.max(0, value - 1));
    }

    const lastQuestion = index + 1 >= totalQuestions;
    const noLives = !isCorrect && lives - 1 <= 0;

    timer.current = window.setTimeout(() => {
      setCelebrate(null);
      if (lastQuestion || noLives) {
        finish(nextCorrect, nextWrong, nextIds);
        return;
      }
      setIndex((value) => value + 1);
      setSelected(null);
      setFeedback(null);
    }, isCorrect ? 950 : 1550);
  };

  const restart = () => {
    window.location.reload();
  };

  if (finished) {
    const percent = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
    const xpEarned = correctCount * 10 + (wrongIds.length === 0 ? 15 : 0);
    const success = percent >= 60;
    return (
      <div className="relative bg-background-50 border border-background-200/50 rounded-2xl p-6 md:p-8 text-center overflow-hidden">
        {success && <Confetti />}
        <div className={`relative w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center zf-pop ${
          success ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-800'
        }`}>
          <i className={`${success ? 'ri-trophy-fill' : 'ri-emotion-sad-line'} text-3xl`}></i>
        </div>
        <h3 className="text-lg md:text-xl font-bold font-heading text-foreground-950">
          {success ? t('cards.result') : t('cards.wrong')}
        </h3>
        <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
          <span className="px-3 py-1 rounded-full bg-background-100 text-foreground-900 text-sm font-bold">
            {correctCount}/{totalQuestions}
          </span>
          <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-sm font-bold">
            +{xpEarned} XP
          </span>
          <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-sm font-bold">
            {percent}%
          </span>
        </div>
        <p className="text-sm text-foreground-500 mt-3 max-w-sm mx-auto">
          {success
            ? 'Belle série ! Les cartes réussies reviendront plus tard, les autres dès demain.'
            : 'Pas de panique : les cartes ratées reviennent tout de suite dans votre file de révision.'}
        </p>

        <div className="max-h-56 overflow-y-auto mt-5 text-left space-y-2">
          {questions.map((question, position) => {
            const ok = correctIds.includes(question.cardId);
            return (
              <div key={question.cardId} className="p-3 rounded-lg bg-background-100/60">
                <p className="text-xs font-semibold text-foreground-800">
                  {position + 1}. {question.question}
                </p>
                <p className="text-[11px] text-foreground-600 mt-1">
                  <i className={ok ? 'ri-checkbox-circle-fill text-accent-600' : 'ri-close-circle-fill text-red-500'}></i>{' '}
                  {question.options[question.correctIndex]}
                </p>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
          <button
            onClick={restart}
            className="px-5 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800"
          >
            <i className="ri-restart-line mr-1.5"></i>
            {t('cards.restart')}
          </button>
          <button
            onClick={onExit}
            className="px-5 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70"
          >
            {t('cards.backToDeck')}
          </button>
        </div>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="bg-background-50 border border-background-200/50 rounded-xl p-8 text-center">
        <p className="text-sm text-foreground-500">Ce jeu ne contient pas assez de cartes pour un quiz.</p>
        <button onClick={onExit} className="mt-4 text-sm font-medium text-primary-700 cursor-pointer">
          Retour
        </button>
      </div>
    );
  }

  return (
    <div className="relative bg-background-50 border border-background-200/50 rounded-2xl overflow-hidden">
      {celebrate && <Confetti />}

      {/* Barre de progression et vies */}
      <div className="px-4 md:px-6 pt-4">
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={onExit}
            className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer flex-shrink-0"
            title={t('cards.backToDeck')}
          >
            <i className="ri-close-line"></i>
          </button>
          <div className="flex-1 h-2.5 rounded-full bg-background-100 overflow-hidden">
            <div
              className="h-full bg-accent-500 transition-all duration-300"
              style={{ width: `${((index + (feedback ? 1 : 0)) / totalQuestions) * 100}%` }}
            ></div>
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {Array.from({ length: 3 }, (_, heart) => (
              <i
                key={heart}
                className={`${heart < lives ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-foreground-300'} text-sm`}
              ></i>
            ))}
          </div>
          <span className="text-[11px] font-bold text-foreground-500 tabular-nums flex-shrink-0">
            {index + 1}/{totalQuestions}
          </span>
        </div>
      </div>

      <div className="px-4 md:px-6 py-5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-400 mb-2">
          {t('cards.question')}
        </p>
        <h3 key={index} className="text-base md:text-lg font-bold font-heading text-foreground-950 mb-5 zf-drop-in">
          {current.question}
        </h3>

        <div key={`options-${index}`} className="space-y-2.5">
          {current.options.map((option, optionIndex) => {
            const isPicked = selected === optionIndex;
            const isRight = optionIndex === current.correctIndex;
            let tone = 'border-background-200/70 bg-background-50 text-foreground-800 hover:border-foreground-300 hover:bg-background-100/60';
            if (feedback && isPicked) tone = isRight ? 'border-accent-400 bg-accent-50 text-accent-900' : 'border-red-400 bg-red-50 text-red-800';
            else if (feedback && isRight) tone = 'border-accent-400 bg-accent-50/70 text-accent-900';
            return (
              <button
                key={optionIndex}
                type="button"
                onClick={() => answer(optionIndex)}
                disabled={feedback !== null}
                className={`w-full flex items-center gap-3 text-left px-4 py-3.5 rounded-xl border-2 text-sm font-medium transition-colors cursor-pointer disabled:cursor-default ${tone} ${
                  feedback && isPicked && !isRight ? 'zf-shake' : ''
                }`}
              >
                <span className="w-7 h-7 rounded-lg bg-background-100 text-[11px] font-bold text-foreground-600 flex items-center justify-center flex-shrink-0">
                  {String.fromCharCode(65 + optionIndex)}
                </span>
                <span className="flex-1 min-w-0">{option}</span>
                {feedback && isRight && <i className="ri-check-line text-accent-600 flex-shrink-0"></i>}
                {feedback && isPicked && !isRight && <i className="ri-close-line text-red-500 flex-shrink-0"></i>}
              </button>
            );
          })}
        </div>

        {feedback && (
          <div
            className={`mt-4 rounded-xl px-4 py-3 flex items-start gap-2.5 zf-pop ${
              feedback === 'correct' ? 'bg-accent-100 text-accent-900' : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            <i className={`${feedback === 'correct' ? 'ri-emotion-happy-line' : 'ri-emotion-sad-line'} text-lg mt-0.5`}></i>
            <div className="min-w-0">
              <p className="text-sm font-bold">{feedback === 'correct' ? t('cards.correct') : t('cards.wrong')}</p>
              <p className="text-xs mt-0.5 leading-relaxed">{current.explanation}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}